// SEO monitoring dashboard. Reads daily snapshots written by
// /api/public/hooks/seo-snapshot and renders trend charts + latest status.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ADMIN_TOKEN_KEY = "seo-monitor:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to view SEO monitor") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}

interface SitemapStat {
  path: string;
  registered: boolean;
  submitted: number;
  indexed: number;
  errors: number;
  warnings: number;
  lastDownloaded?: string;
  error?: string;
}

interface Snapshot {
  id: string;
  captured_at: string;
  robots_ok: boolean;
  robots_status: number | null;
  bing_ok: boolean;
  total_submitted: number;
  total_indexed: number;
  total_errors: number;
  total_warnings: number;
  sitemaps: SitemapStat[];
  notes: string | null;
}

export const Route = createFileRoute("/admin/seo-monitor")({
  head: () => ({
    meta: [
      { title: "SEO Monitor — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoMonitorPage,
});

function SeoMonitorPage() {
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  async function load() {
    setError(null);
    try {
      const res = await fetch("/api/admin/seo-snapshots?limit=90", {
        headers: { "x-admin-token": getAdminToken() },
        cache: "no-store",
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSnapshots(json.snapshots);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function captureNow() {
    setCapturing(true);
    try {
      const res = await fetch("/api/public/hooks/seo-snapshot", { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCapturing(false);
    }
  }

  useEffect(() => { load(); }, []);

  const latest = snapshots?.[0];
  const chartData = (snapshots ?? [])
    .slice()
    .reverse()
    .map((s) => ({
      date: new Date(s.captured_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      submitted: s.total_submitted,
      indexed: s.total_indexed,
      errors: s.total_errors,
      warnings: s.total_warnings,
    }));

  return (
    <div className="container-page py-12 space-y-8">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">SEO Monitor</h1>
          <p className="text-sm text-ink-soft mt-1">
            Automated daily snapshot of robots.txt, sitemap submissions, indexing, and crawl errors.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>Refresh</Button>
          <Button onClick={captureNow} disabled={capturing}>
            {capturing ? "Capturing…" : "Capture snapshot now"}
          </Button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {!snapshots && !error && <p className="text-sm text-ink-soft">Loading…</p>}

      {latest && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatusCard label="robots.txt" ok={latest.robots_ok} sub={`HTTP ${latest.robots_status ?? "?"}`} />
          <StatusCard label="Bing IndexNow" ok={latest.bing_ok} sub={latest.bing_ok ? "key live" : "key missing"} />
          <NumberCard label="URLs submitted" value={latest.total_submitted} />
          <NumberCard label="URLs indexed" value={latest.total_indexed} accent={latest.total_indexed === 0 ? "warn" : "ok"} />
        </section>
      )}

      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Submitted vs Indexed (last {chartData.length} snapshots)</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }} />
                <Legend />
                <Line type="monotone" dataKey="submitted" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="indexed" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Crawl errors & warnings</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }} />
                <Legend />
                <Bar dataKey="errors" fill="hsl(var(--destructive))" />
                <Bar dataKey="warnings" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {latest && (
        <Card>
          <CardHeader>
            <CardTitle>Latest sitemap detail · {new Date(latest.captured_at).toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-soft border-b border-border">
                    <th className="py-2 pr-4">Sitemap</th>
                    <th className="py-2 pr-4">Submitted</th>
                    <th className="py-2 pr-4">Indexed</th>
                    <th className="py-2 pr-4">Errors</th>
                    <th className="py-2 pr-4">Warnings</th>
                    <th className="py-2 pr-4">Last downloaded</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.sitemaps.map((s) => (
                    <tr key={s.path} className="border-b border-border/50">
                      <td className="py-2 pr-4 font-mono text-xs">{s.path.replace("https://arifkhanglobal.com", "")}</td>
                      <td className="py-2 pr-4">{s.submitted}</td>
                      <td className="py-2 pr-4">{s.indexed}</td>
                      <td className="py-2 pr-4">{s.errors}</td>
                      <td className="py-2 pr-4">{s.warnings}</td>
                      <td className="py-2 pr-4 text-xs text-ink-soft">
                        {s.lastDownloaded ? new Date(s.lastDownloaded).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2">
                        {s.error ? (
                          <Badge variant="destructive">{s.error}</Badge>
                        ) : s.registered ? (
                          <Badge>OK</Badge>
                        ) : (
                          <Badge variant="secondary">unregistered</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {latest.notes && (
              <p className="mt-4 text-xs text-ink-soft">Notes: {latest.notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {snapshots && snapshots.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-ink-soft">
            <p>No snapshots yet. Click <strong>Capture snapshot now</strong> to record the first one.</p>
            <p className="text-xs mt-2">A daily cron job runs automatically once scheduled (see plan notes).</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusCard({ label, ok, sub }: { label: string; ok: boolean; sub: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-widest text-ink-soft">{label}</div>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant={ok ? "default" : "destructive"}>{ok ? "OK" : "Fail"}</Badge>
          <span className="text-xs text-ink-soft">{sub}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function NumberCard({ label, value, accent }: { label: string; value: number; accent?: "ok" | "warn" }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-widest text-ink-soft">{label}</div>
        <div className={`mt-2 text-3xl font-semibold tracking-tight ${accent === "warn" ? "text-destructive" : "text-primary"}`}>
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}
